import React, { useState } from 'react';
import T from 'prop-types';
// components
import Modal from 'components/Modal';
import { FormattedMessage } from 'react-intl';
import Button from 'components/Button';
import CircleCheckbox from 'components/CircleCheckbox';
import DateRangePicker from 'components/DatePicker/DateRangePicker/DateRangePicker';
import SingleDatePicker from 'components/DatePicker/SingleDatePicker/SingleDatePicker';
import AsyncSelectLoadMore from 'components/AsyncSelect/AsyncSelectLoadMore';
// redux
import { connect } from 'react-redux';
import { closeModal } from 'reducers/modals';
// endpoints
import { getReportTemplates } from 'endpoints/reportTemplates';
import { getSVRsFarms } from 'endpoints/svr';
// utils
import classnames from 'classnames/bind';
import moment from 'moment';
// styles
import styles from './ReportCSVExportModal.module.scss';

const cn = classnames.bind(styles);

const exportTypes = {
  default: 'default',
  dateRange: 'dateRange',
  oneDay: 'oneDay',
};

const optionKeys = { value: 'id', label: 'name' };

const reportTemplatesGetter = (params) => getReportTemplates(params);
const farmsGetter = (params) => getSVRsFarms(params);

const ReportCSVExportModal = ({ closeModal, onSubmit, title }) => {
  const [exportType, setExportType] = useState(exportTypes.default);
  const [dateRange, setDateRange] = useState({ startDate: undefined, endDate: undefined });
  const [reportType, setReportType] = useState(null);
  const [farm, setFarm] = useState(null);

  const handleExportTypeChange = (type) => {
    setExportType(type);
  };

  const handleDateChange = (date) => {
    if (exportType === exportTypes.oneDay) {
      setDateRange({ startDate: date, endDate: date });
    }
  };

  const handleSubmit = () => {
    const params = { svr_template_id: reportType.value };
    if (farm?.value) params.farm_id = farm.value;
    if (exportType !== exportTypes.default) {
      params.date_start = dateRange.startDate.format('YYYY-MM-DD');
      params.date_end = dateRange.endDate.format('YYYY-MM-DD');
    }
    onSubmit(params);
    closeModal();
  };

  const isExportButtonDisabled = () => {
    if (!reportType?.value) return true;
    if (exportType === exportTypes.dateRange || exportType === exportTypes.oneDay) {
      return !dateRange.startDate || !dateRange.endDate;
    }
    return false;
  };

  return (
    <Modal title={title}>
      <section className="modal-body">
        {/* REPORT TYPE FILTER */}
        <div className="mb-15">
          <label className={cn('filter-label')}>
            <FormattedMessage id="general.reportType" />
          </label>
          <AsyncSelectLoadMore
            loadMoreMode="auto"
            selected={reportType}
            onChange={setReportType}
            optionsGetter={reportTemplatesGetter}
            optionsKeys={optionKeys}
            placeholder={<FormattedMessage id="general.selectReportType" />}
            autoLoad
          />
        </div>
        {/* FARM FILTER */}
        <div className="mb-15">
          <label className={cn('filter-label')}>
            <FormattedMessage id="general.farm" />
          </label>
          <AsyncSelectLoadMore
            loadMoreMode="auto"
            isClearable
            selected={farm}
            onChange={setFarm}
            optionsGetter={farmsGetter}
            optionsKeys={optionKeys}
            placeholder={<FormattedMessage id="general.allFarms" />}
            autoLoad
          />
        </div>
        {/* DEFAULT EXPORT */}
        <div className="mb-10">
          <CircleCheckbox
            label={(
              <span className="semibold">
                <FormattedMessage id="general.defaultExport" />
              </span>
            )}
            checked={exportType === exportTypes.default}
            onChange={() => handleExportTypeChange(exportTypes.default)}
          />
        </div>
        {/* DATE RANGE EXPORT */}
        <div className="mb-10">
          <CircleCheckbox
            label={(
              <span className="semibold">
                <FormattedMessage id="general.exportByPeriod" />
              </span>
            )}
            checked={exportType === exportTypes.dateRange}
            onChange={() => handleExportTypeChange(exportTypes.dateRange)}
          />
          {exportType === exportTypes.dateRange && (
            <DateRangePicker
              className="mt-10"
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onDatesChange={setDateRange}
              withoutOutsideClickHandler
              isOutsideRange={(day) => (day.local().isAfter(moment().local(), 'day'))}
            />
          )}
        </div>
        {/* ONE DAY EXPORT */}
        <div className="mb-10">
          <CircleCheckbox
            label={(
              <span className="semibold">
                <FormattedMessage id="general.exportByDay" />
              </span>
            )}
            checked={exportType === exportTypes.oneDay}
            onChange={() => handleExportTypeChange(exportTypes.oneDay)}
          />
          {exportType === exportTypes.oneDay && (
            <div className="mt-10">
              <SingleDatePicker
                className={cn('sd-picker')}
                date={dateRange.startDate}
                onDateChange={handleDateChange}
                withoutOutsideClickHandler
                isOutsideRange={(day) => (day.local().isAfter(moment().local(), 'day'))}
              />
            </div>
          )}
        </div>
      </section>

      <section className="modal-footer text-right sticky-on-mobile">
        <Button onClick={closeModal}>
          <FormattedMessage id="general.button.cancel" />
        </Button>
        <Button disabled={isExportButtonDisabled()} className="ml-15" primary onClick={handleSubmit}>
          <FormattedMessage id="general.csvImport.results.export" />
        </Button>
      </section>
    </Modal>
  );
};

ReportCSVExportModal.propTypes = {
  closeModal: T.func.isRequired,
  onSubmit: T.func.isRequired,
  title: T.oneOfType([T.string, T.object]).isRequired,
};

export default connect(null, { closeModal })(ReportCSVExportModal);
